import { CARBINE, adsTimeForErgo, type WeaponDef } from "../combat/weapons.js";
import type { GridRect } from "../inventory/grid.js";
import { NEEDS_MAX, type ConsumeDef } from "../sim/needs.js";
import {
  ATTACH_KINDS,
  CALIBER_IDS,
  ITEM_KINDS,
  MAX_MOD_DEPTH,
  type AmmoItemDef,
  type ArmorItemDef,
  type AttachKind,
  type AttachmentItemDef,
  type CaliberId,
  type ContainerItemDef,
  type GrenadeItemDef,
  type ItemDef,
  type LootItemDef,
  type MagazineItemDef,
  type MedItemDef,
  type StatMods,
  type WeaponItemDef,
  type WeaponSlotDef,
} from "./types.js";

/**
 * The item roster. Content, not code: everything is a literal, and the only
 * logic is the registry and `validateItemDb`.
 *
 * Flavour rules: English names that describe the object, never a brand;
 * values in Euro ordered by how pleased you should be to find the thing;
 * honest kilograms (they are a carry limit).
 *
 * Caliber contract (enforced by `validateItemDb`): every weapon caliber has an
 * ammo def, every ammo caliber a weapon, and every magazine a weapon accepts
 * feeds that weapon's caliber.
 */

// ---------------------------------------------------------------------------
// Weapons
// ---------------------------------------------------------------------------

/**
 * The Phase-3 carbine. `stats` is the exported `CARBINE` object itself, so the
 * two cannot drift.
 */
export const ITEM_CARBINE: WeaponItemDef = {
  id: "carbine",
  name: "Hunting carbine",
  shortName: "Carbine",
  kind: "weapon",
  w: 4,
  h: 2,
  // The RECEIVER only: barrel and stock weight/value live on the parts. An
  // assembled carbine is still 3.6 kg and 420 € (`totalWeight`, `fittedValue`).
  weightKg: 2.3,
  valueEuro: 320,
  stackMax: 1,
  description: "A semi-automatic 8×57 hunting carbine. The sort of rifle kept in a Tyrolean village cupboard.",
  stats: CARBINE,
  caliber: "8x57",
  fireMode: "semi",
  weaponClass: "rifle",
  equipSlot: "weapon",
  // THE baseline. Every other gun's kick is authored as a ratio against this
  // one, because "1" has to mean something a player has actually held.
  recoil: 1,
  // A hundred points, flat across the roster (the tooltip reads as a
  // percentage;
  // 500 rounds at `WEAPON_WEAR_PER_SHOT`). Guns already differ by how much they
  // fire, so a per-gun life would pay that difference twice.
  maxDurability: 100,
  // Every slot with a choice: the spawn gun is the one players learn the mod
  // screen on.
  slots: [
    // Least to most magnification — the order the mod screen lists them in.
    { id: "optic", accepts: ["red_dot", "scope_2x", "scope_4x", "scope_8x"] },
    { id: "muzzle", accepts: ["muzzle_brake_8x57"] },
    { id: "grip", accepts: ["foregrip"] },
    // The stock is a real part; an empty slot means it was taken off. Not
    // vital — a stockless carbine fires from the hip.
    {
      id: "stock",
      factoryDefId: "carbine_stock",
      accepts: ["carbine_stock", "recoil_pad", "thumbhole_stock"],
    },
    // Factory box first — see `factoryMagFor`. An empty well is genuinely
    // empty.
    { id: "mag", accepts: ["carbine_mag_30", "carbine_mag_40"] },
    // Appended, not inserted: the fold order above is what every saved carbine
    // was computed in. Vital — a receiver with no barrel is not a gun.
    {
      id: "barrel",
      vital: true,
      factoryDefId: "carbine_barrel",
      accepts: ["carbine_barrel", "carbine_barrel_long"],
    },
  ],
};

/**
 * The only full-auto gun in v1: fast, light, short of penetration — wins rooms,
 * loses streets. `damage`/`penPower` are the unloaded fallback.
 */
const SMG_ERGO = 70;
const SMG_STATS: WeaponDef = {
  id: "smg",
  name: "Submachine gun 9",
  rpm: 650,
  magSize: 25,
  reloadSec: 2.0,
  muzzleVelocity: 380,
  damage: 30,
  penPower: 18,
  falloffStartM: 20,
  falloffEndM: 120,
  falloffMinMult: 0.5,
  // The handiest gun in the roster — short, light, and meant to be swung
  // around a doorframe.
  ergonomics: SMG_ERGO,
  adsTimeSec: adsTimeForErgo(SMG_ERGO),
  hipSpreadDeg: 3,
  zeroRangeM: 30,
};

export const ITEM_SMG: WeaponItemDef = {
  id: "smg",
  name: "Submachine gun 9",
  shortName: "SMG 9",
  kind: "weapon",
  w: 3,
  h: 2,
  // The receiver only, as on the carbine. Assembled: 2.6 kg, 380 €.
  weightKg: 1.8,
  valueEuro: 280,
  stackMax: 1,
  description: "Fully automatic, 9×19, 25 rounds. Unbeatable at ten meters and useless at a hundred.",
  stats: SMG_STATS,
  caliber: "9x19",
  fireMode: "auto",
  weaponClass: "smg",
  equipSlot: "weapon",
  // Two thirds of the carbine's kick per shot — but ten times as many shots per
  // second, so the CLIMB is still the worst in the game. That is the gun.
  recoil: 0.65,
  // The flat 500 shots — see `ITEM_CARBINE`. Burning a magazine per kill is
  // what an SMG costs.
  maxDurability: 100,
  // The only host for the suppressor and the drum: subsonic-ish 9 mm is what a
  // can is worth hanging on, and 45 rounds of it is what makes the gun scary.
  slots: [
    { id: "optic", accepts: ["red_dot"] },
    { id: "muzzle", accepts: ["suppressor_9x19"] },
    { id: "grip", accepts: ["foregrip"] },
    // `wire_stock` is a real part. Not vital — designed to fire folded.
    {
      id: "stock",
      factoryDefId: "wire_stock",
      accepts: ["wire_stock", "recoil_pad", "thumbhole_stock"],
    },
    { id: "mag", accepts: ["smg_mag_25", "smg_drum_45"] },
    // Appended like the carbine's; stub or carbine-length trade around the
    // factory `smg_barrel`. Vital.
    {
      id: "barrel",
      vital: true,
      factoryDefId: "smg_barrel",
      accepts: ["smg_barrel", "smg_barrel_short", "smg_barrel_long"],
    },
    // The `grip` slot above stays on the gun; the rail is for a lamp. The
    // factory `poly_foreend` hosts nothing — `smg_rail_foreend` buys the rail.
    { id: "handguard", factoryDefId: "poly_foreend", accepts: ["poly_foreend", "smg_rail_foreend"] },
  ],
};

/**
 * Pump-action: `rpm` 50 is the pump, and `fireMode` is "semi" because one pull
 * is one shell.
 */
const SHOTGUN_ERGO = 45;
const SHOTGUN_STATS: WeaponDef = {
  id: "shotgun",
  name: "Pump-action shotgun",
  rpm: 50,
  magSize: 5,
  reloadSec: 3.6,
  muzzleVelocity: 400,
  damage: 15,
  penPower: 3,
  falloffStartM: 12,
  falloffEndM: 60,
  falloffMinMult: 0.35,
  // Muzzle-heavy and long in the receiver, but nothing about it is precision
  // kit: a shade below the carbine.
  ergonomics: SHOTGUN_ERGO,
  adsTimeSec: adsTimeForErgo(SHOTGUN_ERGO),
  hipSpreadDeg: 4.5,
  zeroRangeM: 25,
};

export const ITEM_SHOTGUN: WeaponItemDef = {
  id: "shotgun",
  name: "Pump-action shotgun",
  shortName: "Shotgun",
  kind: "weapon",
  w: 4,
  h: 2,
  // The receiver only. Assembled: 3.2 kg, 260 €.
  weightKg: 1.65,
  valueEuro: 170,
  stackMax: 1,
  description: "12 gauge. A door opener with buckshot and a sledgehammer with slugs.",
  stats: SHOTGUN_STATS,
  caliber: "12g",
  fireMode: "semi",
  weaponClass: "shotgun",
  equipSlot: "weapon",
  // The hardest-hitting shove in the roster, and it barely matters: the pump
  // takes 1.2 s, so the sights are back on target long before the next shell.
  recoil: 1.9,
  // The roster's flat 500 shots of life — see `ITEM_CARBINE`.
  maxDurability: 100,
  // You do not swap a tube magazine under fire, you push shells into it. The one
  // gun in the roster that reloads this way; see `WeaponItemDef.reloadStyle`.
  reloadStyle: "topup",
  // Every visible assembly point is exposed to the workbench.
  slots: [
    { id: "optic", accepts: ["red_dot"], defaultName: "Bead sight" },
    { id: "muzzle", accepts: ["ported_choke_12g"], defaultName: "Thread cap" },
    { id: "grip", accepts: ["foregrip"], defaultName: "Pump handguard" },
    // `field_stock` replaced a `defaultName` caption.
    {
      id: "stock",
      factoryDefId: "field_stock",
      accepts: ["field_stock", "recoil_pad", "thumbhole_stock"],
    },
    // The tube is a magazine like any other; factory five-shell first.
    { id: "mag", accepts: ["shotgun_tube_5", "shotgun_tube_8"] },
    // Appended. The coach barrel is the one part that makes a gun worse at
    // hitting on purpose (opens the pattern, +8 handling). Vital.
    {
      id: "barrel",
      vital: true,
      factoryDefId: "shotgun_barrel",
      accepts: ["shotgun_barrel", "shotgun_barrel_coach"],
    },
  ],
};

/**
 * Bolt-action marksman rifle: five rounds, 35 rpm, the flattest trajectory.
 * Zeroed at 150 m — the longest honest sightline in the village.
 */
const DMR_ERGO = 30;
const DMR_STATS: WeaponDef = {
  id: "dmr",
  name: "Hunting rifle",
  rpm: 35,
  magSize: 5,
  reloadSec: 3.2,
  muzzleVelocity: 850,
  damage: 65,
  penPower: 35,
  falloffStartM: 90,
  falloffEndM: 400,
  falloffMinMult: 0.75,
  // The worst handling in the game, and that is the trade: a long heavy barrel
  // under a factory scope is not a thing you bring up in a hurry.
  ergonomics: DMR_ERGO,
  adsTimeSec: adsTimeForErgo(DMR_ERGO),
  hipSpreadDeg: 2.5,
  zeroRangeM: 150,
};

export const ITEM_DMR: WeaponItemDef = {
  id: "dmr",
  name: "Hunting rifle",
  shortName: "Rifle",
  kind: "weapon",
  w: 5,
  h: 2,
  // The receiver only — the biggest carve: barrel, stock and 4× are 2.3 kg
  // and 320 € of parts. Assembled: 4.4 kg, 900 €.
  weightKg: 2.1,
  valueEuro: 580,
  stackMax: 1,
  description: "A scoped bolt-action rifle in 8×57. Five rounds, and every one counts.",
  stats: DMR_STATS,
  caliber: "8x57",
  fireMode: "semi",
  weaponClass: "marksman",
  equipSlot: "weapon",
  // A full-power cartridge out of a light hunting stock. Combined with the
  // worst ergonomics in the game it is the least forgiving gun to shoot fast.
  recoil: 1.7,
  // The flat 500 shots — the gun a player will own longest.
  maxDurability: 100,
  // The receiver remains the serialized base item, while every visible
  // assembly point can take a replacement. Factory parts are named explicitly.
  slots: [
    // The factory glass is `factory_scope_4x`, a real part carrying the zoom
    // (replacing a caption plus `defaultZoom`). Ladder unchanged.
    {
      id: "optic",
      factoryDefId: "factory_scope_4x",
      accepts: ["factory_scope_4x", "red_dot", "scope_2x", "scope_4x", "scope_8x"],
    },
    { id: "muzzle", accepts: ["muzzle_brake_8x57"], defaultName: "Thread cap" },
    { id: "grip", accepts: ["foregrip"], defaultName: "Hunting fore-end" },
    // PROTOCOL v19: the walnut was a caption and is now `walnut_stock`. Not vital
    // — the rifle fires off a bipod or a windowsill without it.
    {
      id: "stock",
      factoryDefId: "walnut_stock",
      accepts: ["walnut_stock", "recoil_pad", "thumbhole_stock"],
    },
    // The magazine comes out like every other mag-fed gun's.
    { id: "mag", accepts: ["rifle_mag_5", "rifle_mag_10"] },
    // Appended. The match barrel: +8 handling for 4 % velocity, displacing the
    // factory `rifle_barrel`. Vital.
    {
      id: "barrel",
      vital: true,
      factoryDefId: "rifle_barrel",
      accepts: ["rifle_barrel", "rifle_barrel_match"],
    },
  ],
};

/**
 * The sidearm, the only gun that goes in the holster. Small (two cells, under
 * a kilo, shortest sight radius) paid for with the fastest handling and reload.
 * Shares the SMG's 9×19 — a gun for finishing a fight, not opening one.
 */
const PISTOL_ERGO = 80;
const PISTOL_STATS: WeaponDef = {
  id: "pistol",
  name: "Service pistol 9",
  rpm: 400,
  magSize: 15,
  reloadSec: 1.6,
  muzzleVelocity: 360,
  damage: 28,
  penPower: 16,
  falloffStartM: 15,
  falloffEndM: 80,
  falloffMinMult: 0.5,
  // The handiest thing in the game by a clear margin: it is the gun you draw
  // when there is no time to do anything else, so it has to come up first.
  ergonomics: PISTOL_ERGO,
  adsTimeSec: adsTimeForErgo(PISTOL_ERGO),
  hipSpreadDeg: 3.5,
  zeroRangeM: 25,
};

export const ITEM_PISTOL: WeaponItemDef = {
  id: "pistol",
  name: "Service pistol 9",
  shortName: "Pistol 9",
  kind: "weapon",
  w: 2,
  h: 1,
  // The frame only — the smallest carve (0.1 kg, 25 € of barrel). Assembled:
  // 0.9 kg, 180 €.
  weightKg: 0.8,
  valueEuro: 155,
  stackMax: 1,
  description: "Fifteen rounds of 9×19 on the belt. A gendarmerie sidearm: light, plain, and always there.",
  stats: PISTOL_STATS,
  caliber: "9x19",
  fireMode: "semi",
  weaponClass: "pistol",
  // THE holster gun. `canEquipInSlot` reads this and lets it into `pistol` and
  // nowhere else — a sidearm in a shoulder slot would be a second primary.
  equipSlot: "pistol",
  // A shade under the SMG per shot, and semi-automatic, so the climb never
  // compounds: the lightest gun in the roster is also the calmest to shoot.
  recoil: 0.6,
  // The roster's flat 500 shots of life — see `ITEM_CARBINE`.
  maxDurability: 100,
  // Three slots, each a thing a pistol actually wears. One magazine with no
  // upgrade — a sidearm that could be built up would stop being a compromise.
  slots: [
    { id: "optic", accepts: ["red_dot"] },
    { id: "muzzle", accepts: ["suppressor_9x19"] },
    { id: "mag", accepts: ["pistol_mag_15"] },
    // A lamp under the dust cover. Non-vital, no factory part — a bare rail.
    { id: "tactical", accepts: ["flashlight", "laser"] },
    // Appended. Accepts exactly its own part; exists so "every gun has a vital
    // barrel" holds without an exception.
    { id: "barrel", vital: true, factoryDefId: "pistol_barrel", accepts: ["pistol_barrel"] },
  ],
};

/**
 * The Eastern carbine: a surplus self-loader, and the rifle the modular bench
 * was built for — six slots, three vital, two of the parts hosting parts of
 * their own. Weight and value were never carved: 3.9 kg and 760 € have always
 * been the receiver's. Between the SMG and the carbine in every number: 600 rpm
 * of a cartridge harder than 9×19 and softer than 8×57, kicking harder than
 * either. The classic trade is in the `receiver` slot: the factory cover carries
 * nothing, glass needs the railed one.
 */
const EAST_CARBINE_ERGO = 52;
const EAST_CARBINE_STATS: WeaponDef = {
  id: "east_carbine",
  name: "Eastern carbine",
  rpm: 600,
  magSize: 30,
  reloadSec: 2.6,
  muzzleVelocity: 715,
  damage: 44,
  penPower: 24,
  falloffStartM: 40,
  falloffEndM: 250,
  falloffMinMult: 0.6,
  // Nose-heavy, stamped, and built for a conscript in mittens: handier than the
  // shotgun, nowhere near the submachine gun it otherwise resembles.
  ergonomics: EAST_CARBINE_ERGO,
  adsTimeSec: adsTimeForErgo(EAST_CARBINE_ERGO),
  hipSpreadDeg: 3.6,
  zeroRangeM: 100,
};

export const ITEM_EAST_CARBINE: WeaponItemDef = {
  id: "east_carbine",
  name: "Eastern carbine",
  shortName: "EC-47",
  kind: "weapon",
  w: 4,
  h: 2,
  weightKg: 3.9,
  valueEuro: 760,
  stackMax: 1,
  description:
    "A stamped Eastern self-loader in 7.62×39 that came over the pass with somebody's grandfather. Thirty rounds, no manners, and it has never once failed to go off.",
  stats: EAST_CARBINE_STATS,
  caliber: "7.62x39",
  fireMode: "auto",
  weaponClass: "assault",
  equipSlot: "weapon",
  // The worst SUSTAINED climb in the game: a full intermediate round at ten a
  // second.
  recoil: 1.15,
  // The flat 500 shots — see `ITEM_CARBINE`.
  maxDurability: 100,
  // SIX SLOTS, in fold order, and the three vital ones first because that is the
  // order the bench should read as "the rifle" before "the trimmings".
  slots: [
    // Vital: with the cover off the bolt carrier is loose. The factory part
    // hosts nothing; the railed one is where every optic lives.
    {
      id: "receiver",
      vital: true,
      factoryDefId: "stamped_top_cover",
      accepts: ["stamped_top_cover", "railed_top_cover"],
    },
    // VITAL, for the obvious reason, and a host: the muzzle thread is on the
    // barrel, so the brake belongs to the barrel and leaves with it.
    { id: "barrel", vital: true, factoryDefId: "ec_barrel", accepts: ["ec_barrel"] },
    // VITAL: there is no trigger guard to hold onto without it.
    {
      id: "pistolgrip",
      vital: true,
      factoryDefId: "bakelite_grip",
      accepts: ["bakelite_grip", "ergo_grip"],
    },
    // NOT vital — the gas tube is exposed and the rifle fires perfectly well
    // while it cooks your hand. The railed guard is the second host.
    {
      id: "handguard",
      factoryDefId: "wood_handguard",
      accepts: ["wood_handguard", "railed_handguard"],
    },
    // NOT vital either: a rifle with no stock is a rifle fired from the hip.
    { id: "stock", factoryDefId: "wood_stock", accepts: ["wood_stock", "folding_stock"] },
    // Factory box first — see `factoryMagFor`. No `factoryDefId` on a `mag`
    // slot (`validateItemDb` refuses it).
    { id: "mag", accepts: ["ec_mag_30", "ec_mag_10"] },
  ],
};

/**
 * The sniper rifle: a modern bolt gun on an aluminium chassis, and the first
 * gun born modular — six slots each wearing a factory part, the receiver's
 * weight and value simply what is left over. 500 m of useful range, 80 damage
 * and 45 pen out of a 900 m/s 8×57, bought with the worst handling anywhere
 * (22 ergo) and a 30 rpm bolt. Zeroed at 200 m — a gun for the hillsides.
 * Shares the hunting rifle's 8×57 and both its magazines.
 */
const SNIPER_ERGO = 22;
const SNIPER_STATS: WeaponDef = {
  id: "sniper",
  name: "Sniper rifle",
  rpm: 30,
  magSize: 5,
  reloadSec: 3.4,
  muzzleVelocity: 900,
  damage: 80,
  penPower: 45,
  falloffStartM: 120,
  falloffEndM: 500,
  falloffMinMult: 0.8,
  // The worst handling in the game, eight points under the hunting rifle.
  ergonomics: SNIPER_ERGO,
  adsTimeSec: adsTimeForErgo(SNIPER_ERGO),
  hipSpreadDeg: 3.0,
  zeroRangeM: 200,
};

export const ITEM_SNIPER: WeaponItemDef = {
  id: "sniper",
  name: "Sniper rifle",
  shortName: "Sniper",
  kind: "weapon",
  w: 6,
  h: 2,
  // The receiver, computed as a remainder: the assembled rifle is 6.2 kg and
  // 1500 €.
  //   weight  1.8 barrel + 0.05 nut + 1.4 chassis + 0.15 grip + 0.4 handguard
  //           + 0.8 glass = 4.6, so 6.2 − 4.6 = 1.6
  //   value   220 + 15 + 260 + 40 + 60 + 520 = 1115, so 1500 − 1115 = 385
  // `totalWeight`/`factoryValueEur` fold the tree (the nut is the depth-2
  // term).
  weightKg: 1.6,
  valueEuro: 385,
  stackMax: 1,
  description:
    "A bolt-action rifle bedded into an aluminium chassis, in 8×57. Five rounds, ten-power glass, and there is nowhere in the valley it cannot reach.",
  stats: SNIPER_STATS,
  caliber: "8x57",
  fireMode: "semi",
  weaponClass: "sniper",
  equipSlot: "weapon",
  // Level with the shotgun's shove; at 30 rpm it barely matters.
  recoil: 1.9,
  // The flat 500 shots — see `ITEM_CARBINE`.
  maxDurability: 100,
  // Six slots, six factory parts, in fold order. Two vital, two hosts.
  slots: [
    // Vital, and a host like `ec_barrel`: the thread is cut into the barrel.
    // Both ship with the nut on.
    {
      id: "barrel",
      vital: true,
      factoryDefId: "sniper_barrel",
      accepts: ["sniper_barrel", "sniper_barrel_short"],
    },
    // NOT vital — a chassis-less rifle is an action and a barrel you can still
    // fire off a windowsill, which is the same ruling every other stock gets.
    {
      id: "stock",
      factoryDefId: "sniper_chassis",
      accepts: ["sniper_chassis", "sniper_chassis_folding", "thumbhole_stock"],
    },
    // VITAL, exactly as the Eastern carbine's is: the chassis has no trigger
    // guard to hold onto without a grip bolted under it.
    {
      id: "pistolgrip",
      vital: true,
      factoryDefId: "sniper_grip",
      accepts: ["sniper_grip", "ergo_grip"],
    },
    // The second host. The railed tube costs two points of handling.
    {
      id: "handguard",
      factoryDefId: "sniper_handguard",
      accepts: ["sniper_handguard", "sniper_rail_handguard"],
    },
    // The factory 10× first, then the ladder below it — trading down is a
    // real choice on a 22-ergo rifle.
    {
      id: "optic",
      factoryDefId: "sniper_scope_10x",
      accepts: ["sniper_scope_10x", "scope_8x", "scope_4x", "scope_2x", "red_dot"],
    },
    // The hunting rifle's two boxes, shared. No `factoryDefId` on a `mag` slot.
    { id: "mag", accepts: ["rifle_mag_5", "rifle_mag_10"] },
  ],
};

// ---------------------------------------------------------------------------
// Ammunition
//
// 8x57 FMJ is exactly the Phase-3 carbine's numbers (53/30/×1) — the default
// loadout spawns with it. Everything else trades one of the three for another.
// ---------------------------------------------------------------------------

function ammo(
  id: string,
  name: string,
  shortName: string,
  caliber: CaliberId,
  damage: number,
  penPower: number,
  velocityMult: number,
  pellets: number,
  weightKg: number,
  valueEuro: number,
  stackMax: number,
  description: string,
): AmmoItemDef {
  return {
    id,
    name,
    shortName,
    kind: "ammo",
    w: 1,
    h: 1,
    weightKg,
    valueEuro,
    stackMax,
    description,
    caliber,
    damage,
    penPower,
    velocityMult,
    pellets,
  };
}

export const ITEM_AMMO_8X57_FMJ = ammo(
  "ammo_8x57_fmj",
  "8×57 full metal jacket",
  "8×57 FMJ",
  "8x57",
  53,
  30,
  1,
  1,
  0.025,
  6,
  60,
  "The village's everyday cartridge. Goes through wood and scratches armor plates.",
);

export const ITEM_AMMO_8X57_SP = ammo(
  "ammo_8x57_sp",
  "8×57 soft point",
  "8×57 SP",
  "8x57",
  65,
  18,
  0.95,
  1,
  0.026,
  9,
  60,
  "A hunting bullet that tears through unprotected targets but catches on every vest.",
);

export const ITEM_AMMO_9X19_FMJ = ammo(
  "ammo_9x19_fmj",
  "9×19 full metal jacket",
  "9×19 FMJ",
  "9x19",
  30,
  18,
  1,
  1,
  0.012,
  3,
  60,
  "Standard-issue ammunition. Plenty of it, but little power per shot.",
);

export const ITEM_AMMO_9X19_HP = ammo(
  "ammo_9x19_hp",
  "9×19 hollow point",
  "9×19 HP",
  "9x19",
  40,
  8,
  0.95,
  1,
  0.012,
  5,
  60,
  "Far nastier against soft targets — completely useless against anything else.",
);

export const ITEM_AMMO_12G_BUCK = ammo(
  "ammo_12g_buck",
  "12/70 buckshot",
  "12 buckshot",
  "12g",
  15,
  3,
  1,
  8,
  0.045,
  4,
  20,
  "Eight pellets per shell, each weak on its own. At five meters, that hardly matters.",
);

export const ITEM_AMMO_12G_SLUG = ammo(
  "ammo_12g_slug",
  "12/70 slug",
  "12 slug",
  "12g",
  60,
  12,
  1.05,
  1,
  0.05,
  7,
  20,
  "One solid piece of lead. Hammers through doors and rib cages alike.",
);

/**
 * 7.62×39: between the two calibres the valley knows — 44 damage at 24 pen, out
 * of the one gun that fires it thirty times without stopping.
 */
export const ITEM_AMMO_762_FMJ = ammo(
  "ammo_762_fmj",
  "7.62×39 full metal jacket",
  "7.62 FMJ",
  "7.62x39",
  44,
  24,
  1,
  1,
  0.018,
  5,
  60,
  "Steel-cased, lacquered green, packed in a paper sleeve with foreign writing on it. There is a great deal of it about.",
);

export const ITEM_AMMO_762_HP = ammo(
  "ammo_762_hp",
  "7.62×39 hollow point",
  "7.62 HP",
  "7.62x39",
  55,
  10,
  0.95,
  1,
  0.018,
  7,
  60,
  "A soft-nosed reload for the same case, made up for deer. Devastating on anything that is not wearing a plate.",
);

// ---------------------------------------------------------------------------
// Attachments
//
// Sixty parts across the blocks below. The balance rule: nothing is a pure
// upgrade — every part that helps costs something (a 4× buys magnification
// with −12 ergo, the drum buys 20 rounds with a 30 % slower reload).
//
// Eleven are MAGAZINES, the only stateful parts: `magCapacity`, one `caliber`,
// rounds on the instance. Every gun lists its factory box first (plain, cheap,
// the capacity the receiver was authored with) plus at most one upgrade.
//
// `ergoAdd` is the handling currency, spread over optic, muzzle, grip and
// stock so a build is a budget; `adsTimeForErgo` turns the total into seconds
// once. `recoilMult` is client-side feel only.
// ---------------------------------------------------------------------------

function attachment(
  id: string,
  name: string,
  shortName: string,
  attachKind: AttachKind,
  w: number,
  h: number,
  weightKg: number,
  valueEuro: number,
  modifiers: StatMods,
  description: string,
): AttachmentItemDef {
  return {
    id,
    name,
    shortName,
    kind: "attachment",
    w,
    h,
    weightKg,
    valueEuro,
    // Attachments are unique objects with a slot to sit in; they never stack.
    stackMax: 1,
    description,
    attachKind,
    modifiers,
  };
}

/**
 * A true magazine: `attachment` plus the two stateful fields, narrowed to
 * present. `modifiers` is routinely empty here — a factory magazine changes no
 * handling number; what it does is hold thirty rounds.
 */
function magazine(
  id: string,
  name: string,
  shortName: string,
  w: number,
  h: number,
  weightKg: number,
  valueEuro: number,
  magCapacity: number,
  caliber: CaliberId,
  modifiers: StatMods,
  description: string,
): MagazineItemDef {
  return {
    ...attachment(id, name, shortName, "mag", w, h, weightKg, valueEuro, modifiers, description),
    magCapacity,
    caliber,
  };
}

export const ITEM_RED_DOT = attachment(
  "red_dot",
  "Red dot sight",
  "Red dot",
  "optic",
  1,
  1,
  0.2,
  120,
  { ergoAdd: 8 },
  "A glowing dot on glass. No magnification, but faster to shoulder.",
);

export const ITEM_SCOPE_4X = attachment(
  "scope_4x",
  "Rifle scope 4×",
  "Scope 4×",
  "optic",
  2,
  1,
  0.55,
  340,
  { ergoAdd: -12, zoom: 4, zeroRangeSetM: 100 },
  "Four-power, zeroed at a hundred meters. At ten meters, it is a window into nothing.",
);

/**
 * The glass ladder either side of the 4×: magnification buys reach and spends
 * ergonomics and money. Zeroing follows — 50, 100, 150 m.
 */
export const ITEM_SCOPE_2X = attachment(
  "scope_2x",
  "Rifle scope 2×",
  "Scope 2×",
  "optic",
  2,
  1,
  0.35,
  200,
  { ergoAdd: -6, zoom: 2, zeroRangeSetM: 50 },
  "A low-power hunting glass, zeroed at fifty meters. Twice as close, and still useful across a yard.",
);

export const ITEM_SCOPE_8X = attachment(
  "scope_8x",
  "Rifle scope 8×",
  "Scope 8×",
  "optic",
  2,
  1,
  0.75,
  620,
  { ergoAdd: -20, zoom: 8, zeroRangeSetM: 150 },
  "Eight-power, zeroed at a hundred and fifty. The far hillside comes close; the room you are standing in disappears.",
);

export const ITEM_MUZZLE_BRAKE_8X57 = attachment(
  "muzzle_brake_8x57",
  "8×57 muzzle brake",
  "Brake",
  "muzzle",
  1,
  1,
  0.15,
  90,
  { recoilMult: 0.75 },
  "Vents gas sideways. The shooter notices immediately, and so does anyone beside them.",
);

export const ITEM_SUPPRESSOR_9X19 = attachment(
  "suppressor_9x19",
  "9×19 suppressor",
  "Suppressor",
  "muzzle",
  2,
  1,
  0.45,
  260,
  { suppressed: true, muzzleVelocityMult: 0.95, recoilMult: 0.85, ergoAdd: -6 },
  "Turns the bang into a crack. Costs a little velocity, a lot of money, and some of the gun's balance.",
);

export const ITEM_PORTED_CHOKE_12G = attachment(
  "ported_choke_12g",
  "12 gauge ported choke",
  "Ported choke",
  "muzzle",
  1,
  1,
  0.18,
  95,
  { recoilMult: 0.8, hipSpreadMult: 0.92 },
  "A ported choke for the shotgun muzzle. Tames the kick and tightens the pattern slightly.",
);

export const ITEM_FOREGRIP = attachment(
  "foregrip",
  "Foregrip",
  "Grip",
  "grip",
  1,
  1,
  0.18,
  70,
  { hipSpreadMult: 0.8, recoilMult: 0.9, ergoAdd: 6 },
  "A piece of turned wood under the barrel. It makes shooting from the hip surprisingly effective.",
);

export const ITEM_RECOIL_PAD = attachment(
  "recoil_pad",
  "Recoil pad",
  "Pad",
  "stock",
  1,
  1,
  0.25,
  60,
  { recoilMult: 0.85, ergoAdd: 4 },
  "A laced rubber cap over the stock. Cheap, light, and noticeably effective.",
);

export const ITEM_THUMBHOLE_STOCK = attachment(
  "thumbhole_stock",
  "Thumbhole stock",
  "Thumbhole",
  "stock",
  2,
  1,
  0.5,
  210,
  { recoilMult: 0.7, ergoAdd: -8 },
  "A heavy target stock with a thumbhole. Steady as a rock and just as sluggish.",
);

// --- 8×57: the carbine's box and the rifle's -------------------------------

export const ITEM_CARBINE_MAG_30 = magazine(
  "carbine_mag_30",
  "8×57 box magazine",
  "Mag 30",
  1,
  2,
  0.35,
  45,
  30,
  "8x57",
  {},
  "Thirty rounds of 8×57 in a plain steel box. The magazine the carbine was sold with, and the one everybody has a spare of.",
);

export const ITEM_CARBINE_MAG_40 = magazine(
  "carbine_mag_40",
  "8×57 extended magazine",
  "Mag 40",
  1,
  2,
  0.45,
  110,
  40,
  "8x57",
  { reloadSecMult: 1.15 },
  "Ten rounds longer than the standard box, and it sticks out far enough that you notice on every reload.",
);

export const ITEM_RIFLE_MAG_5 = magazine(
  "rifle_mag_5",
  "8×57 rifle magazine",
  "Mag 5",
  1,
  1,
  0.15,
  25,
  5,
  "8x57",
  {},
  "Five rounds in a flush-fitting box. Nothing about a hunting rifle was ever meant to hold more.",
);

export const ITEM_RIFLE_MAG_10 = magazine(
  "rifle_mag_10",
  "8×57 deep magazine",
  "Mag 10",
  1,
  2,
  0.25,
  90,
  10,
  "8x57",
  // Bigger box, slower to seat — the same trade `carbine_mag_40` and the
  // drum make, so no magazine in the roster buys rounds for nothing.
  { ergoAdd: -2, reloadSecMult: 1.1 },
  "Twice the rounds, hanging a hand's width below the stock. It works, and it snags on everything.",
);

// --- 9×19: the submachine gun's and the sidearm's --------------------------

export const ITEM_SMG_MAG_25 = magazine(
  "smg_mag_25",
  "9×19 stick magazine",
  "Mag 25",
  1,
  2,
  0.3,
  35,
  25,
  "9x19",
  {},
  "A straight stick of twenty-five. Light, cheap, and there are crates of them somewhere in the valley.",
);

export const ITEM_SMG_DRUM_45 = magazine(
  "smg_drum_45",
  "9×19 drum magazine",
  "Drum",
  2,
  1,
  0.7,
  300,
  45,
  "9x19",
  { reloadSecMult: 1.3, ergoAdd: -6 },
  "Forty-five rounds without a break. Bulky, noisy to carry, and worth every euro indoors.",
);

export const ITEM_PISTOL_MAG_15 = magazine(
  "pistol_mag_15",
  "9×19 pistol magazine",
  "Mag 15",
  1,
  1,
  0.15,
  20,
  15,
  "9x19",
  {},
  "Fifteen rounds for the service pistol. The cheapest part in the valley and the one worth carrying two of.",
);

// --- 12 gauge: the shotgun's tube, and the long one -------------------------

export const ITEM_SHOTGUN_TUBE_5 = magazine(
  "shotgun_tube_5",
  "12 gauge shell tube",
  "Tube 5",
  1,
  1,
  0.2,
  15,
  5,
  "12g",
  {},
  "The tube the shotgun was built around. Five shells, pushed in one at a time, and no faster than that.",
);

export const ITEM_SHOTGUN_TUBE_8 = magazine(
  "shotgun_tube_8",
  "12 gauge long tube",
  "Tube 8",
  2,
  1,
  0.3,
  80,
  8,
  "12g",
  { reloadSecMult: 1.15 },
  "A longer tube reaching past the muzzle. Three more shells, and three more to push in when it runs dry.",
);

// ---------------------------------------------------------------------------
// Barrels, handguards and the things that hang off a rail
//
// A gun is a tree, not a receiver with lumps: three of the parts below are
// HOSTS with `slots` of their own. Nothing is a pure upgrade — a long barrel
// buys velocity with handling, a rail costs a point or two for the privilege.
// Every barrel here DISPLACES a factory barrel that is an item in its own
// right,
// so what comes off is a part you can hold and what is left is a receiver
// `weaponComplete` refuses.
// ---------------------------------------------------------------------------

export const ITEM_CARBINE_BARREL_LONG = attachment(
  "carbine_barrel_long",
  "Long hunting barrel",
  "Hunt barrel",
  "barrel",
  2,
  1,
  0.7,
  130,
  { muzzleVelocityMult: 1.06, ergoAdd: -6 },
  "A longer, heavier tube threaded for the same brake. Six percent more speed out the front and a rifle that no longer swings.",
);

export const ITEM_SMG_BARREL_SHORT = attachment(
  "smg_barrel_short",
  "Short 9×19 barrel",
  "Stub barrel",
  "barrel",
  1,
  1,
  0.25,
  65,
  { muzzleVelocityMult: 0.94, ergoAdd: 6 },
  "Cut down to just past the chamber. It loses the last of the powder's work and gains a gun you can turn around inside a stairwell.",
);

export const ITEM_SMG_BARREL_LONG = attachment(
  "smg_barrel_long",
  "Long 9×19 barrel",
  "MP barrel",
  "barrel",
  2,
  1,
  0.4,
  105,
  { muzzleVelocityMult: 1.05, ergoAdd: -4 },
  "The carbine-length barrel the factory offered and nobody in the valley bought. It makes a submachine gun reach, at the cost of the reason to carry one.",
);

export const ITEM_SHOTGUN_BARREL_COACH = attachment(
  "shotgun_barrel_coach",
  "Coach barrel",
  "Coach",
  "barrel",
  2,
  1,
  0.8,
  85,
  { hipSpreadMult: 1.12, ergoAdd: 8 },
  "Sawn back to the fore-end, the way the mail coaches carried them. The pattern opens up like a thrown bucket of water, and it comes up faster than anything else in the valley.",
);

export const ITEM_RIFLE_BARREL_MATCH = attachment(
  "rifle_barrel_match",
  "Heavy match barrel",
  "Match barrel",
  "barrel",
  3,
  1,
  1.3,
  280,
  { muzzleVelocityMult: 1.04, ergoAdd: -8 },
  "A thick, slow-heating competition barrel. It shoots better than the man behind it and weighs like an argument.",
);

/**
 * The Eastern carbine's factory barrel, the first host in the roster: no
 * modifiers (it IS the barrel `def.stats` counts), but it carries the `muzzle`
 * slot, so stripping it takes the brake with it. Also the only depth-2 factory
 * part — its thread protector is minted by `mintFactoryMods` recursing here.
 */
export const ITEM_EC_BARREL: AttachmentItemDef = {
  ...attachment(
    "ec_barrel",
    "Standard 7.62 barrel",
    "Std barrel",
    "barrel",
    2,
    1,
    0.9,
    80,
    {},
    "Chrome-lined, pinned to the front trunnion, threaded at the muzzle. Sixty years old and utterly indifferent about it.",
  ),
  slots: [
    { id: "muzzle", factoryDefId: "thread_protector", accepts: ["thread_protector", "slant_brake"] },
  ],
};

/**
 * The nut on the thread, cheapest part in the game. Exists so the brake has
 * something to displace.
 */
export const ITEM_THREAD_PROTECTOR = attachment(
  "thread_protector",
  "Thread protector",
  "Thread nut",
  "muzzle",
  1,
  1,
  0.05,
  15,
  {},
  "A knurled steel nut over the muzzle thread, staked in place at a factory that no longer exists. It does nothing except stop the thread from being ruined.",
);

export const ITEM_SLANT_BRAKE = attachment(
  "slant_brake",
  "Slant muzzle brake",
  "Slant brake",
  "muzzle",
  1,
  1,
  0.12,
  75,
  { recoilMult: 0.82, ergoAdd: -1 },
  "A cut steel cylinder that vents up and to one side. It exists to fight the climb of a full-automatic burst, and it does, loudly.",
);

/**
 * The stamped top cover: no slots — nothing that must hold a zero may be
 * screwed to it, so this rifle has irons and nothing else.
 */
export const ITEM_STAMPED_TOP_COVER = attachment(
  "stamped_top_cover",
  "Stamped top cover",
  "Top cover",
  "receiver",
  2,
  1,
  0.2,
  30,
  {},
  "A folded sheet of steel over the bolt carrier. It comes off with a thumb and goes back on crooked, which is why nobody ever mounted a scope on one.",
);

export const ITEM_RAILED_TOP_COVER: AttachmentItemDef = {
  ...attachment(
    "railed_top_cover",
    "Railed top cover",
    "Rail cover",
    "receiver",
    2,
    1,
    0.35,
    190,
    { ergoAdd: -2 },
    "A milled cover hinged to the rear trunnion with a rail down its spine. It holds a zero, it weighs more than the rifle deserves, and it is the only way to put glass on one.",
  ),
  slots: [{ id: "optic", accepts: ["red_dot", "scope_2x"] }],
};

export const ITEM_BAKELITE_GRIP = attachment(
  "bakelite_grip",
  "Bakelite pistol grip",
  "Bakelite",
  "pistolgrip",
  1,
  1,
  0.12,
  25,
  {},
  "Orange-brown plastic with a moulded seam up the back. Cold in winter, slippery in summer, and it will outlive everybody who has held it.",
);

export const ITEM_ERGO_GRIP = attachment(
  "ergo_grip",
  "Ergonomic pistol grip",
  "Sport grip",
  "pistolgrip",
  1,
  1,
  0.15,
  95,
  { ergoAdd: 7 },
  "A rubber-backed grip with a shelf under the web of the thumb. Somebody in the valley makes them in a garage, and they are worth every euro.",
);

export const ITEM_WOOD_HANDGUARD = attachment(
  "wood_handguard",
  "Wooden handguard",
  "Wood guard",
  "handguard",
  2,
  1,
  0.3,
  35,
  {},
  "Two pieces of laminated birch clamped around the gas tube. Warm to hold and there is nowhere on it to put anything.",
);

export const ITEM_RAILED_HANDGUARD: AttachmentItemDef = {
  ...attachment(
    "railed_handguard",
    "Railed handguard",
    "Rail guard",
    "handguard",
    2,
    1,
    0.45,
    160,
    { ergoAdd: -2 },
    "An alloy fore-end with rails on three faces. There is nowhere on it a bare hand will stay, so it comes with a foregrip or it does not come at all.",
  ),
  slots: [
    // Vital, and the only vital slot not on a gun: you cannot hold the rifle
    // by bare rails, so fitting this commits you to a foregrip. Ships with none
    // on purpose. Also what proves `weaponComplete` is recursive — take the
    // handguard off and the rifle is complete again.
    { id: "grip", vital: true, accepts: ["foregrip"] },
    { id: "tactical", accepts: ["flashlight", "laser"] },
  ],
};

/**
 * The SMG's railed fore-end. Hosts a light only — the `grip` slot stays on the
 * gun.
 */
export const ITEM_SMG_RAIL_FOREEND: AttachmentItemDef = {
  ...attachment(
    "smg_rail_foreend",
    "Railed fore-end",
    "Rail fore-end",
    "handguard",
    2,
    1,
    0.25,
    120,
    { ergoAdd: -1 },
    "A polymer shell over the barrel nut with a short rail underneath. It adds nothing to how the gun shoots and everything to what you can hang on it.",
  ),
  slots: [{ id: "tactical", accepts: ["flashlight", "laser"] }],
};

export const ITEM_WOOD_STOCK = attachment(
  "wood_stock",
  "Wooden rifle stock",
  "Wood stock",
  "stock",
  2,
  1,
  0.6,
  40,
  {},
  "A shellacked birch club with a steel buttplate. It was shaped for a man in a greatcoat and it still fits one.",
);

export const ITEM_FOLDING_STOCK = attachment(
  "folding_stock",
  "Folding stock",
  "Folder",
  "stock",
  2,
  1,
  0.5,
  150,
  { ergoAdd: 5, recoilMult: 1.1 },
  "A stamped underfolder that hinges away beneath the receiver. Quicker into a doorway, and it kicks like something you have annoyed.",
);

/**
 * The two things that hang off a rail. What T switches on is a beam, not a
 * stat: the lamp's effect is where the light falls, and `laser`'s
 * `hipSpreadMult` is unconditional — a diode on a rail steadies the hands
 * whether or not it is lit.
 */
export const ITEM_FLASHLIGHT = attachment(
  "flashlight",
  "Weapon flashlight",
  "Lamp",
  "tactical",
  1,
  1,
  0.15,
  55,
  { ergoAdd: -1 },
  "An alloy torch in a rail clamp, wired to a pressure pad. Press T and it throws a cone down whatever you are pointing at, which is also a cone pointing back at you.",
);

export const ITEM_LASER = attachment(
  "laser",
  "Laser pointer",
  "Laser",
  "tactical",
  1,
  1,
  0.1,
  140,
  { hipSpreadMult: 0.93 },
  "A red diode zeroed at twenty-five meters. Your hands know where the barrel is pointing whether or not it is switched on, and that is most of shooting from the hip; press T and everyone else finds out too.",
);

// --- 7.62×39: the Eastern carbine's boxes --------------------------------------

export const ITEM_EC_MAG_30 = magazine(
  "ec_mag_30",
  "7.62×39 curved magazine",
  "Mag 30",
  1,
  2,
  0.4,
  50,
  30,
  "7.62x39",
  {},
  "Thirty rounds in a ribbed steel banana. There is a crate of them in every second barn between here and the border, and nobody remembers who left them.",
);

export const ITEM_EC_MAG_10 = magazine(
  "ec_mag_10",
  "7.62×39 hunting magazine",
  "Mag 10",
  1,
  1,
  0.2,
  30,
  10,
  "7.62x39",
  // The one magazine that buys handling with rounds: flush, a third of the
  // capacity.
  { ergoAdd: 3 },
  "A ten-round box, because that is what the game laws allow. Flush with the receiver, quick out of a coat pocket, and empty far too soon.",
);

// ---------------------------------------------------------------------------
// The factory components of the five v1 guns
//
// Eleven parts that were on those guns all along and are now items, so every
// gun is a receiver plus parts and the bench shows the same tree for all six.
// Every modifier block here is EMPTY (a factory part IS the gun `def.stats`
// describes) except `factory_scope_4x`, which carries the `zoom` that used to
// be `defaultZoom`. Weight and value were carved out of the receivers, so an
// assembled gun totals what it always did (carbine 2.3 + 0.7 + 0.6 = 3.6 kg,
// 320 + 60 + 40 = 420 €) and `buyPrice` never moved.
// ---------------------------------------------------------------------------

export const ITEM_CARBINE_BARREL = attachment(
  "carbine_barrel",
  "Standard hunting barrel",
  "Std barrel",
  "barrel",
  2,
  1,
  0.7,
  60,
  {},
  "The tube the carbine was sold with, blued and stamped with a proof mark from a town two valleys over. Nobody has ever had a reason to take one off.",
);

export const ITEM_CARBINE_STOCK = attachment(
  "carbine_stock",
  "Walnut hunting stock",
  "Walnut",
  "stock",
  2,
  1,
  0.6,
  40,
  {},
  "Oiled walnut with a cheekpiece worn pale by sixty years of chins. It fits the rifle it came on and nothing else in the valley quite the same way.",
);

export const ITEM_SMG_BARREL = attachment(
  "smg_barrel",
  "Standard 9×19 barrel",
  "Std 9 barrel",
  "barrel",
  1,
  1,
  0.3,
  45,
  {},
  "A stubby pressed-steel tube behind the front sight. It is exactly as long as it needs to be and not a millimetre more.",
);

export const ITEM_WIRE_STOCK = attachment(
  "wire_stock",
  "Wire folding stock",
  "Wire stock",
  "stock",
  2,
  1,
  0.35,
  30,
  {},
  "Two bent rods and a rubber butt pad, hinged so the whole thing tucks under the receiver. It was never comfortable and it was never meant to be.",
);

export const ITEM_POLY_FOREEND = attachment(
  "poly_foreend",
  "Polymer fore-end",
  "Poly guard",
  "handguard",
  2,
  1,
  0.15,
  25,
  {},
  "A ribbed plastic half-shell clipped over the barrel nut. There is nowhere on it to put anything, and for most of the valley that has always been fine.",
);

export const ITEM_SHOTGUN_BARREL = attachment(
  "shotgun_barrel",
  "Standard 12 gauge barrel",
  "Std 12 barrel",
  "barrel",
  3,
  1,
  1.0,
  55,
  {},
  "Twenty-eight inches of plain 12 gauge with a brass bead at the end. It has shot more clay and more crows than anything else in the district.",
);

export const ITEM_FIELD_STOCK = attachment(
  "field_stock",
  "Field stock",
  "Field stock",
  "stock",
  2,
  1,
  0.55,
  35,
  {},
  "Beech, varnished the colour of weak tea, with a sling swivel screwed into the toe. It has been dropped in more barnyards than anyone has counted.",
);

export const ITEM_RIFLE_BARREL = attachment(
  "rifle_barrel",
  "Long stalking barrel",
  "Stalk barrel",
  "barrel",
  3,
  1,
  1.1,
  90,
  {},
  "A slim, long tube tapered almost to nothing at the muzzle. Everything the rifle does across a valley, it does because of this.",
);

export const ITEM_WALNUT_STOCK = attachment(
  "walnut_stock",
  "Walnut rifle stock",
  "Walnut stock",
  "stock",
  2,
  1,
  0.7,
  50,
  {},
  "Dark walnut with a schnabel fore-end and a hand-cut chequer that has gone smooth in two places. Somebody's grandfather paid for this and it shows.",
);

/**
 * The hunting rifle's factory glass: `zoom` and nothing else — the rifle's 30
 * ergo and 150 m zero already describe a rifle wearing it.
 */
export const ITEM_FACTORY_SCOPE_4X = attachment(
  "factory_scope_4x",
  "Factory 4× scope",
  "Factory 4×",
  "optic",
  2,
  1,
  0.5,
  180,
  { zoom: 4 },
  "A fixed four-power in steel rings, fitted at the shop that sold the rifle and never taken off since. The reticle is a plain cross with one thick post, and the glass is yellow at the edges.",
);

export const ITEM_PISTOL_BARREL = attachment(
  "pistol_barrel",
  "Service pistol barrel",
  "Pistol barrel",
  "barrel",
  1,
  1,
  0.1,
  25,
  {},
  "Four inches of chromed steel with a locking lug on top. It drops out with the slide and there is nothing else in the valley it would fit.",
);

// ---------------------------------------------------------------------------
// The sniper rifle's components
//
// Nine parts: six it ships with, three upgrades. The factory six carry nothing
// (the rifle's 22 ergo and 200 m zero are the numbers of a rifle wearing them)
// except `sniper_scope_10x`'s `zoom`; the sixth is the Eastern carbine's
// `thread_protector`. The upgrades obey the rule — short barrel: +6 handling
// for 6 % velocity; folding chassis: +4 for 5 % more kick; railed tube: −2.
// ---------------------------------------------------------------------------

/**
 * The heavy factory barrel, a host (the thread belongs to it). Accepts a
 * SUPPRESSOR, which no other 8×57 barrel does — most of why a player buys the
 * rifle rather than building the hunting one up.
 */
export const ITEM_SNIPER_BARREL: AttachmentItemDef = {
  ...attachment(
    "sniper_barrel",
    "Heavy sniper barrel",
    "Sniper barrel",
    "barrel",
    4,
    1,
    1.8,
    220,
    {},
    "A thick fluted tube in stainless steel, threaded at the muzzle and free-floated the whole way back to the action. It weighs what it weighs because a barrel that flexes is a barrel that lies.",
  ),
  slots: [
    {
      id: "muzzle",
      factoryDefId: "thread_protector",
      accepts: ["thread_protector", "muzzle_brake_8x57", "suppressor_8x57"],
    },
  ],
};

/**
 * The same barrel cut back: +6 handling for 6 % velocity — the only way this
 * rifle is a gun for a street.
 */
export const ITEM_SNIPER_BARREL_SHORT: AttachmentItemDef = {
  ...attachment(
    "sniper_barrel_short",
    "Short sniper barrel",
    "Short barrel",
    "barrel",
    3,
    1,
    1.3,
    260,
    { ergoAdd: 6, muzzleVelocityMult: 0.94 },
    "Chopped and re-threaded by somebody who decided the far hillside was not the fight they were in. It is louder, it is slower, and it comes around a corner.",
  ),
  slots: [
    {
      id: "muzzle",
      factoryDefId: "thread_protector",
      accepts: ["thread_protector", "muzzle_brake_8x57", "suppressor_8x57"],
    },
  ],
};

/**
 * The only can for a full-power cartridge; the 9×19 one's numbers, a different
 * price, and one barrel it fits.
 */
export const ITEM_SUPPRESSOR_8X57 = attachment(
  "suppressor_8x57",
  "8×57 suppressor",
  "Suppressor 8",
  "muzzle",
  2,
  1,
  0.6,
  420,
  { suppressed: true, muzzleVelocityMult: 0.95, recoilMult: 0.85, ergoAdd: -6 },
  "A welded steel can the length of a forearm. It does not make the rifle quiet — nothing makes 8×57 quiet — but it takes the crack off the front of the shot, and a crack is what tells people where you are.",
);

export const ITEM_SNIPER_CHASSIS = attachment(
  "sniper_chassis",
  "Aluminium chassis",
  "Chassis",
  "stock",
  3,
  1,
  1.4,
  260,
  {},
  "A milled alloy beam with a squared butt and a comb you can actually get behind. Cold to hold in February and utterly indifferent to the weather otherwise, which is the whole argument against walnut.",
);

export const ITEM_SNIPER_CHASSIS_FOLDING = attachment(
  "sniper_chassis_folding",
  "Folding chassis",
  "Folding chassis",
  "stock",
  3,
  1,
  1.5,
  340,
  { ergoAdd: 4, recoilMult: 1.05 },
  "The same beam on a hinge, so the rifle goes into a pack instead of over a shoulder. The joint has a little play in it, and you feel every bit of that play through your cheek.",
);

export const ITEM_SNIPER_GRIP = attachment(
  "sniper_grip",
  "Vertical rifle grip",
  "Rifle grip",
  "pistolgrip",
  1,
  1,
  0.15,
  40,
  {},
  "A near-vertical polymer grip bolted under the chassis. It puts the wrist straight behind the trigger, which is what a rifle fired slowly and deliberately wants.",
);

export const ITEM_SNIPER_HANDGUARD = attachment(
  "sniper_handguard",
  "Tube handguard",
  "Tube guard",
  "handguard",
  2,
  1,
  0.4,
  60,
  {},
  "A plain alloy tube clamped to the front of the action with the barrel floating inside it. Smooth all the way round, and there is nowhere on it to put anything.",
);

export const ITEM_SNIPER_RAIL_HANDGUARD: AttachmentItemDef = {
  ...attachment(
    "sniper_rail_handguard",
    "Railed tube handguard",
    "Rail guard",
    "handguard",
    2,
    1,
    0.55,
    210,
    { ergoAdd: -2 },
    "The same tube with rails machined along the top and the bottom of it. Heavier, colder, and the only place on this rifle a bipod, a lamp or a hand that is not yours can be bolted.",
  ),
  slots: [
    // Not vital, unlike the Eastern railed guard: a tube with strips still
    // gives the hand somewhere to go.
    { id: "grip", accepts: ["foregrip"] },
    { id: "tactical", accepts: ["flashlight", "laser"] },
  ],
};

/**
 * The rifle's own glass: `zoom` only, like `factory_scope_4x`. Useless inside a
 * house, hence the four smaller sights the slot accepts.
 */
export const ITEM_SNIPER_SCOPE_10X = attachment(
  "sniper_scope_10x",
  "Sniper scope 10×",
  "Scope 10×",
  "optic",
  3,
  1,
  0.8,
  520,
  { zoom: 10 },
  "Ten power in a fat matte tube on tall rings, with a mil-marked cross and turrets somebody has already dialled and taped. At two hundred meters you can count buttons; at twenty you cannot find the barn.",
);

// ---------------------------------------------------------------------------
// Armour — the class-3 vest's `maxDurability` 40 matches Phase 3's default
// vest.
// ---------------------------------------------------------------------------

const TORSO_ZONES = ["thorax", "stomach"] as const;
const HEAD_ZONES = ["head"] as const;

export const ITEM_VEST_LODEN: ArmorItemDef = {
  id: "vest_loden",
  name: "Loden armor vest",
  shortName: "Loden vest",
  kind: "armor",
  w: 3,
  h: 3,
  weightKg: 3.4,
  valueEuro: 180,
  stackMax: 1,
  description: "A poacher's vest with sewn-in steel plates. Better than nothing, but only just.",
  equipSlot: "vest",
  armorClass: 2,
  maxDurability: 30,
  zones: TORSO_ZONES,
};

export const ITEM_VEST_GENDARM: ArmorItemDef = {
  id: "vest_gendarm",
  name: "Gendarmerie armor vest",
  shortName: "Service vest",
  kind: "armor",
  w: 3,
  h: 3,
  weightKg: 6,
  valueEuro: 520,
  stackMax: 1,
  description: "The standard PMC vest. Stops an 8×57 FMJ round about half the time.",
  equipSlot: "vest",
  armorClass: 3,
  maxDurability: 40,
  zones: TORSO_ZONES,
};

export const ITEM_VEST_PLATTE: ArmorItemDef = {
  id: "vest_platte",
  name: "Nordkette plate carrier",
  shortName: "Plate carrier",
  kind: "armor",
  w: 4,
  h: 3,
  weightKg: 9.2,
  valueEuro: 1400,
  stackMax: 1,
  description: "Ceramic plates front and back. Heavy as a bag of cement and worth every kilogram.",
  equipSlot: "vest",
  armorClass: 4,
  maxDurability: 55,
  zones: TORSO_ZONES,
};

export const ITEM_HELMET_STAHL: ArmorItemDef = {
  id: "helmet_stahl",
  name: "Steel helmet",
  shortName: "Steel helmet",
  kind: "armor",
  w: 2,
  h: 2,
  weightKg: 1.5,
  valueEuro: 140,
  stackMax: 1,
  description: "A museum piece from the attic. Stops buckshot; asking more would be unwise.",
  equipSlot: "helmet",
  armorClass: 2,
  maxDurability: 25,
  zones: HEAD_ZONES,
};

export const ITEM_HELMET_GENDARM: ArmorItemDef = {
  id: "helmet_gendarm",
  name: "Gendarmerie helmet",
  shortName: "Service helmet",
  kind: "armor",
  w: 2,
  h: 2,
  weightKg: 2.1,
  valueEuro: 640,
  stackMax: 1,
  description: "An aramid helmet with an optic rail. The difference between a headshot and a headache.",
  equipSlot: "helmet",
  armorClass: 3,
  maxDurability: 35,
  zones: HEAD_ZONES,
};

// ---------------------------------------------------------------------------
// Medical
// ---------------------------------------------------------------------------

export const ITEM_MED_VERBAND: MedItemDef = {
  id: "med_verband",
  name: "Field dressing",
  shortName: "Bandage",
  kind: "med",
  w: 1,
  h: 1,
  weightKg: 0.1,
  valueEuro: 25,
  stackMax: 1,
  description: "Stops bleeding. Three uses, two seconds each — and you must stand still.",
  useTimeSec: 2,
  charges: 3,
  effect: { t: "stopBleed" },
};

export const ITEM_MED_SCHIENE: MedItemDef = {
  id: "med_schiene",
  name: "Splint",
  shortName: "Splint",
  kind: "med",
  w: 1,
  h: 2,
  weightKg: 0.2,
  valueEuro: 40,
  stackMax: 1,
  description: "Sets a fracture. Single use; without it, you limp for the rest of the raid.",
  useTimeSec: 4,
  charges: 1,
  effect: { t: "fixFracture" },
};

export const ITEM_MED_ERSTEHILFE: MedItemDef = {
  id: "med_erstehilfe",
  name: "First aid pouch",
  shortName: "First aid",
  kind: "med",
  w: 2,
  h: 1,
  weightKg: 0.35,
  valueEuro: 90,
  stackMax: 1,
  description: "Restores 30 HP to the most injured limb per use. Does not stop bleeding.",
  useTimeSec: 3,
  charges: 4,
  effect: { t: "heal", hpPerCharge: 30, alsoStopsBleed: false },
};

export const ITEM_MED_SANIKASTEN: MedItemDef = {
  id: "med_sanikasten",
  name: "Medical kit",
  shortName: "Med kit",
  kind: "med",
  w: 2,
  h: 2,
  weightKg: 1.4,
  valueEuro: 260,
  stackMax: 1,
  description: "The kit from the fire station: restores 50 HP per use and stops bleeding too.",
  useTimeSec: 4,
  charges: 6,
  effect: { t: "heal", hpPerCharge: 50, alsoStopsBleed: true },
};

// ---------------------------------------------------------------------------
// Grenades
//
// The frag kills, the smoke erases a sightline, the flash buys three seconds.
// None stacks or has an equip slot. The fuses are the balance: 3.5 s on the
// frag is long enough to throw back and short enough to cook; the flash's
// 1.5 s barely leaves the hand.
// ---------------------------------------------------------------------------

export const ITEM_GRENADE_FRAG: GrenadeItemDef = {
  id: "gren_frag",
  name: "Frag Grenade",
  shortName: "Frag",
  kind: "grenade",
  w: 1,
  h: 1,
  weightKg: 0.4,
  valueEuro: 45,
  stackMax: 1,
  description:
    "Army stock that came over the pass in a crate. Three and a half seconds from the pin, and eight metres of nobody standing up.",
  fuseSec: 3.5,
  effect: { t: "frag", radiusM: 8, damage: 110 },
};

export const ITEM_GRENADE_SMOKE: GrenadeItemDef = {
  id: "gren_smoke",
  name: "Smoke Grenade",
  shortName: "Smoke",
  kind: "grenade",
  w: 1,
  h: 1,
  weightKg: 0.45,
  valueEuro: 20,
  stackMax: 1,
  description:
    "A signal canister off a fire engine. Half a minute of grey where the street used to be — and nobody in it can see you either.",
  fuseSec: 2,
  effect: { t: "smoke", radiusM: 6, durationSec: 30 },
};

export const ITEM_GRENADE_FLASH: GrenadeItemDef = {
  id: "gren_flash",
  name: "Flashbang",
  shortName: "Flash",
  kind: "grenade",
  w: 1,
  h: 1,
  weightKg: 0.3,
  valueEuro: 30,
  stackMax: 1,
  description:
    "A bang and a white wall. Four seconds of somebody else's blindness, if they were looking — and the fuse gives you barely time to throw it.",
  fuseSec: 1.5,
  effect: { t: "flash", radiusM: 10, durationSec: 4 },
};

// ---------------------------------------------------------------------------
// Rigs, backpacks and storage boxes
//
// One def type, three shapes: a RIG (chest, small), a BACKPACK (the looter's
// career ladder), a BOX (`equipSlot: "none"`, carried inside something else).
// Boxes are the only defs with a `filter`, and earn their huge capacity by it.
// ---------------------------------------------------------------------------

/**
 * The starter rig: four separate 1x2 magazine pouches, not one open tray. Same
 * eight cells, different shape — the 2x1 drum does not fit a pouch lying down
 * (it does turned on its side, which `findFreePlacement` tries second).
 */
export const ITEM_RIG_PATRONENGURT: ContainerItemDef = {
  id: "rig_patronengurt",
  name: "Cartridge rig",
  shortName: "Rig",
  kind: "container",
  w: 2,
  h: 2,
  weightKg: 0.9,
  valueEuro: 150,
  stackMax: 1,
  description:
    "Four magazine pouches across the chest. A stick or a banana each; nothing wider goes in.",
  equipSlot: "rig",
  gridW: 4,
  gridH: 2,
  layout: [
    { x: 0, y: 0, w: 1, h: 2 },
    { x: 1, y: 0, w: 1, h: 2 },
    { x: 2, y: 0, w: 1, h: 2 },
    { x: 3, y: 0, w: 1, h: 2 },
  ],
};

/**
 * The rig you buy once you have something worth carrying: three 1x2 mag
 * pockets, a 2x2 utility pouch that takes a med kit whole, and two 1x1 pockets.
 */
export const ITEM_RIG_BRUSTGURT: ContainerItemDef = {
  id: "rig_brustgurt",
  name: "Chest rig",
  shortName: "Chest rig",
  kind: "container",
  w: 3,
  h: 2,
  weightKg: 1.4,
  valueEuro: 420,
  stackMax: 1,
  description:
    "Army-surplus webbing a hunter has re-stitched twice: three magazine pouches, a deep utility pocket and two small ones on the flank.",
  equipSlot: "rig",
  gridW: 6,
  gridH: 2,
  layout: [
    { x: 0, y: 0, w: 1, h: 2 },
    { x: 1, y: 0, w: 1, h: 2 },
    { x: 2, y: 0, w: 1, h: 2 },
    { x: 3, y: 0, w: 2, h: 2 },
    { x: 5, y: 0, w: 1, h: 1 },
    { x: 5, y: 1, w: 1, h: 1 },
  ],
};

export const ITEM_PACK_WANDER: ContainerItemDef = {
  id: "pack_wander",
  name: "Hiking backpack",
  shortName: "Backpack",
  kind: "container",
  w: 3,
  h: 3,
  weightKg: 1.1,
  valueEuro: 220,
  stackMax: 1,
  description: "A weathered twenty-liter backpack with 9 slots. A looter's first step.",
  equipSlot: "backpack",
  gridW: 3,
  gridH: 3,
};

export const ITEM_PACK_JAGD: ContainerItemDef = {
  id: "pack_jagd",
  name: "Hunting backpack",
  shortName: "Hunting pack",
  kind: "container",
  w: 4,
  h: 4,
  weightKg: 2.4,
  valueEuro: 780,
  stackMax: 1,
  description: "Sixteen slots on a carrying frame. Enough to haul out an entire living room.",
  equipSlot: "backpack",
  gridW: 4,
  gridH: 4,
};

export const ITEM_PACK_TOURER: ContainerItemDef = {
  id: "pack_tourer",
  name: "Tourenrucksack",
  shortName: "Tourer",
  kind: "container",
  w: 4,
  h: 5,
  weightKg: 3.0,
  valueEuro: 1100,
  stackMax: 1,
  description:
    "A ski-touring pack with a stiff back panel and twenty slots. Tall enough that you feel it in every doorway.",
  equipSlot: "backpack",
  gridW: 4,
  gridH: 5,
};

export const ITEM_PACK_EXPEDITION: ContainerItemDef = {
  id: "pack_expedition",
  name: "Expeditionsrucksack",
  shortName: "Expedition",
  kind: "container",
  w: 5,
  h: 6,
  weightKg: 4.4,
  valueEuro: 2400,
  stackMax: 1,
  description:
    "Thirty slots on an alloy frame, built for a week above the treeline. The biggest thing anyone in this valley wears on their back.",
  equipSlot: "backpack",
  gridW: 5,
  gridH: 6,
};

/** The med-only box: 2x2 outside, sixteen cells inside. */
export const ITEM_MEDBOX: ContainerItemDef = {
  id: "medbox",
  name: "Medizinkoffer",
  shortName: "Medbox",
  kind: "container",
  w: 2,
  h: 2,
  weightKg: 1.0,
  valueEuro: 600,
  stackMax: 1,
  description:
    "An enameled first-aid case with a red cross worn half away. Sixteen slots, and only medical supplies go inside.",
  equipSlot: "none",
  gridW: 4,
  gridH: 4,
  filter: ["med"],
};

/**
 * The ammo-only box: the medbox's shape, filtered to cartridges; a small
 * armoury, hence the price.
 */
export const ITEM_AMMOBOX: ContainerItemDef = {
  id: "ammobox",
  name: "Munitionskiste",
  shortName: "Ammo box",
  kind: "container",
  w: 2,
  h: 2,
  weightKg: 2.0,
  valueEuro: 700,
  stackMax: 1,
  description:
    "A green steel cartridge chest with a fold-down latch. Sixteen slots, and only ammunition goes inside.",
  equipSlot: "none",
  gridW: 4,
  gridH: 4,
  filter: ["ammo"],
};

/** Graggy's answer to a rucksack full of loose spark plugs. */
export const ITEM_JUNKBOX: ContainerItemDef = {
  id: "junkbox",
  name: "Graggy's Junk Box",
  shortName: "Junk Box",
  kind: "container",
  w: 3,
  h: 3,
  weightKg: 6.0,
  valueEuro: 2600,
  stackMax: 1,
  description:
    "A battered crate Graggy nailed back together twice. Barter goods and hideout materials only — he says he has no interest in what else you drag out of that valley.",
  equipSlot: "none",
  gridW: 8,
  gridH: 8,
  filter: ["loot"],
};

// ---------------------------------------------------------------------------
// Barter goods
// ---------------------------------------------------------------------------

function loot(
  id: string,
  name: string,
  shortName: string,
  w: number,
  h: number,
  weightKg: number,
  valueEuro: number,
  stackMax: number,
  description: string,
): LootItemDef {
  return { id, name, shortName, kind: "loot", w, h, weightKg, valueEuro, stackMax, description };
}

/**
 * A grocery: barter good first, food second (see `LootItemDef.consume`).
 * `consume` is per unit of the stack.
 */
function edible(def: LootItemDef, consume: ConsumeDef): LootItemDef {
  return { ...def, consume };
}

/**
 * The chamois beard, the only boss-only item: in no loot table, on no shelf, in
 * no scav's pockets — only on the man `rollRoleLoadout` builds for the boss.
 * Worth 400 € at the Wirtin's; one cell and 150 g so it never competes with
 * the loot beside it.
 */
export const ITEM_GAMSBART: LootItemDef = loot(
  "gamsbart",
  "Chamois beard",
  "Gamsbart",
  1,
  1,
  0.15,
  400,
  1,
  "A hand's width of chamois hair bound in a silver ferrule, cut from an animal somebody climbed for. Nobody in the valley buys one of these. They are shot for.",
);

export const LOOT_ITEMS: readonly LootItemDef[] = [
  // The Spar's shelf: barter goods that are also the answer to a long raid.
  // Meals, not snacks — finding food should stop the thinking. Brandy is the
  // only DRINK, which makes thirst the harder need (`sim/needs.ts`).
  edible(
    loot("speck", "Slab of bacon", "Bacon", 2, 1, 1.2, 12, 2, "Smoked, long-lasting, and a currency of its own in the village. A meal, cut with a knife."),
    { useTimeSec: 3, energy: 35, hydration: 0 },
  ),
  edible(
    loot("bergkaese", "Wheel of mountain cheese", "Cheese", 2, 2, 2.4, 20, 1, "Half a wheel from the dairy's cold room. Heavy, and enough to eat for a day."),
    { useTimeSec: 4, energy: 55, hydration: 0 },
  ),
  edible(
    loot("schnaps", "Fruit brandy", "Brandy", 1, 2, 0.9, 15, 1, "Home-distilled. Trade good, disinfectant, and comfort in a bottle — and the only thing on the shelf you can drink."),
    { useTimeSec: 2, energy: 0, hydration: 35 },
  ),
  loot("zigaretten", "Pack of cigarettes", "Cigarettes", 1, 1, 0.03, 8, 5, "The second currency. Everyone wants them; no one gives them away."),
  loot("naegel", "Box of nails", "Nails", 1, 1, 0.6, 4, 3, "One kilogram of carpenter's nails."),
  loot("zuendkerzen", "Spark plug set", "Spark plugs", 1, 1, 0.2, 9, 4, "Four pieces, still boxed, from the workshop."),
  loot("kupferdraht", "Copper wire coil", "Copper wire", 1, 2, 1.1, 14, 2, "Heavy, bulky, and always in demand."),
  loot("batterie", "Battery", "Battery", 1, 1, 0.4, 7, 4, "Half empty, but power is power."),
  loot("taschenuhr", "Pocket watch", "Pocket watch", 1, 1, 0.1, 45, 1, "Silver case, still ticking. Taken from a drawer whose owner will miss it."),
  loot("jagdmesser", "Hunting knife", "Knife", 1, 2, 0.35, 30, 1, "Stag-horn handle, freshly sharpened blade."),
  edible(
    loot("honigglas", "Jar of honey", "Honey", 1, 1, 0.7, 10, 2, "Alpine honey, unopened. Eaten from the jar with a finger, it is sugar and a little water."),
    { useTimeSec: 3, energy: 25, hydration: 10 },
  ),
  // Game: the meat is the only edible not on a shop shelf, and a barter good at
  // the Wirtin's. The Krickel is a trophy.
  edible(
    loot("meat_hare", "Hare", "Hare", 1, 1, 1, 18, 2, "Skinned and gutted in the field. A thin meal, and it cooks over anything."),
    { useTimeSec: 5, energy: 20, hydration: 0 },
  ),
  edible(
    loot("meat_venison", "Venison haunch", "Venison", 2, 1, 3.2, 45, 1, "A hind leg of roe deer or chamois, still warm. The Wirtin pays for it by the kilo."),
    { useTimeSec: 6, energy: 45, hydration: 0 },
  ),
  loot("gamskrickel", "Chamois horns", "Krickel", 1, 1, 0.4, 180, 1, "The hooked horns of a chamois on a slice of skull. The trophy that goes over the Stammtisch, and proof of a shot nobody saw you take."),
  loot("feuerzeug", "Lighter", "Lighter", 1, 1, 0.03, 3, 5, "Petrol lighter engraved with an edelweiss."),
  loot("streichhoelzer", "Matches", "Matches", 1, 1, 0.02, 2, 10, "A box from the village inn."),
  loot("schraubenzieher", "Screwdriver", "Screwdriver", 1, 2, 0.25, 6, 2, "Phillips head with a worn wooden handle."),
  loot("benzinkanister", "Fuel canister", "Fuel", 1, 2, 4.5, 30, 1, "Twenty liters siphoned out of a farmyard tank. The generator drinks it and gives back light."),
  loot("silberkreuz", "Silver crucifix", "Crucifix", 1, 2, 0.6, 150, 1, "From the sacristy. You should not take it. You do anyway."),
  // Boss-only. See the note on the const above for why it is not in a table.
  ITEM_GAMSBART,
  /**
   * Cash — a `loot` item so it is dragged, split and stacked by the same code;
   * `stackMax` big enough for a good raid in one cell.
   */
  loot("euro", "Euro", "€", 1, 1, 0.001, 1, 500, "Cash. The only item whose value is not up for debate."),
  /**
   * Graggy's bottle, the first quest item. A `loot` item so it moves through
   * the same code as everything else; `questItem` puts it outside the economy
   * (0 €, no shelf, no loot table, no buyer — turn-in only). `validatePrices`,
   * `validateTraders` and `validateItemDb` each name the exemption.
   */
  {
    ...loot(
      "graggy_obstler",
      "Graggy's Obstler",
      "Obstler",
      1,
      2,
      0.9,
      0,
      1,
      "A bottle of pear brandy with Graggy's mark burned into the cork. He wants it back, and he wants it unopened.",
    ),
    questItem: true as const,
  },
];

// ---------------------------------------------------------------------------
// The registry
// ---------------------------------------------------------------------------

export const WEAPON_ITEMS: readonly WeaponItemDef[] = [
  ITEM_CARBINE,
  ITEM_SMG,
  ITEM_SHOTGUN,
  ITEM_DMR,
  ITEM_PISTOL,
  ITEM_EAST_CARBINE,
  ITEM_SNIPER,
];

export const AMMO_ITEMS: readonly AmmoItemDef[] = [
  ITEM_AMMO_8X57_FMJ,
  ITEM_AMMO_8X57_SP,
  ITEM_AMMO_9X19_FMJ,
  ITEM_AMMO_9X19_HP,
  ITEM_AMMO_12G_BUCK,
  ITEM_AMMO_12G_SLUG,
  ITEM_AMMO_762_FMJ,
  ITEM_AMMO_762_HP,
];

export const ATTACHMENT_ITEMS: readonly AttachmentItemDef[] = [
  ITEM_RED_DOT,
  ITEM_SCOPE_2X,
  ITEM_SCOPE_4X,
  ITEM_SCOPE_8X,
  ITEM_MUZZLE_BRAKE_8X57,
  ITEM_SUPPRESSOR_9X19,
  ITEM_PORTED_CHOKE_12G,
  ITEM_FOREGRIP,
  ITEM_RECOIL_PAD,
  ITEM_THUMBHOLE_STOCK,
  ITEM_CARBINE_MAG_30,
  ITEM_CARBINE_MAG_40,
  ITEM_RIFLE_MAG_5,
  ITEM_RIFLE_MAG_10,
  ITEM_SMG_MAG_25,
  ITEM_SMG_DRUM_45,
  ITEM_PISTOL_MAG_15,
  ITEM_SHOTGUN_TUBE_5,
  ITEM_SHOTGUN_TUBE_8,
  // Protocol v18: barrels, handguards, grips, covers and the rail furniture.
  ITEM_CARBINE_BARREL_LONG,
  ITEM_SMG_BARREL_SHORT,
  ITEM_SMG_BARREL_LONG,
  ITEM_SHOTGUN_BARREL_COACH,
  ITEM_RIFLE_BARREL_MATCH,
  ITEM_EC_BARREL,
  ITEM_THREAD_PROTECTOR,
  ITEM_SLANT_BRAKE,
  ITEM_STAMPED_TOP_COVER,
  ITEM_RAILED_TOP_COVER,
  ITEM_BAKELITE_GRIP,
  ITEM_ERGO_GRIP,
  ITEM_WOOD_HANDGUARD,
  ITEM_RAILED_HANDGUARD,
  ITEM_SMG_RAIL_FOREEND,
  ITEM_WOOD_STOCK,
  ITEM_FOLDING_STOCK,
  ITEM_FLASHLIGHT,
  ITEM_LASER,
  ITEM_EC_MAG_30,
  ITEM_EC_MAG_10,
  // Protocol v19: the five v1 guns' own barrels, stocks, fore-end and glass —
  // the parts they were always described as wearing, now items that come off.
  ITEM_CARBINE_BARREL,
  ITEM_CARBINE_STOCK,
  ITEM_SMG_BARREL,
  ITEM_WIRE_STOCK,
  ITEM_POLY_FOREEND,
  ITEM_SHOTGUN_BARREL,
  ITEM_FIELD_STOCK,
  ITEM_RIFLE_BARREL,
  ITEM_WALNUT_STOCK,
  ITEM_FACTORY_SCOPE_4X,
  ITEM_PISTOL_BARREL,
  // The sniper rifle: six factory components and three upgrades, the first gun
  // in the roster whose parts were authored before its receiver was.
  ITEM_SNIPER_BARREL,
  ITEM_SNIPER_BARREL_SHORT,
  ITEM_SUPPRESSOR_8X57,
  ITEM_SNIPER_CHASSIS,
  ITEM_SNIPER_CHASSIS_FOLDING,
  ITEM_SNIPER_GRIP,
  ITEM_SNIPER_HANDGUARD,
  ITEM_SNIPER_RAIL_HANDGUARD,
  ITEM_SNIPER_SCOPE_10X,
];

export const ARMOR_ITEMS: readonly ArmorItemDef[] = [
  ITEM_VEST_LODEN,
  ITEM_VEST_GENDARM,
  ITEM_VEST_PLATTE,
  ITEM_HELMET_STAHL,
  ITEM_HELMET_GENDARM,
];

export const MED_ITEMS: readonly MedItemDef[] = [
  ITEM_MED_VERBAND,
  ITEM_MED_SCHIENE,
  ITEM_MED_ERSTEHILFE,
  ITEM_MED_SANIKASTEN,
];

export const GRENADE_ITEMS: readonly GrenadeItemDef[] = [
  ITEM_GRENADE_FRAG,
  ITEM_GRENADE_SMOKE,
  ITEM_GRENADE_FLASH,
];

export const CONTAINER_ITEMS: readonly ContainerItemDef[] = [
  ITEM_RIG_PATRONENGURT,
  ITEM_RIG_BRUSTGURT,
  ITEM_PACK_WANDER,
  ITEM_PACK_JAGD,
  ITEM_PACK_TOURER,
  ITEM_PACK_EXPEDITION,
  ITEM_MEDBOX,
  ITEM_AMMOBOX,
  ITEM_JUNKBOX,
];

/** Every def in the game, in a fixed order. Iteration order is the wire order
 *  of nothing, but it IS what tests and loot-table validation walk. */
export const ALL_ITEM_DEFS: readonly ItemDef[] = [
  ...WEAPON_ITEMS,
  ...AMMO_ITEMS,
  ...ATTACHMENT_ITEMS,
  ...ARMOR_ITEMS,
  ...MED_ITEMS,
  ...GRENADE_ITEMS,
  ...CONTAINER_ITEMS,
  ...LOOT_ITEMS,
];

/**
 * Registry keyed by id. A `Map`, so `getItemDef("toString")` is `null` rather
 * than a function.
 */
export const ITEM_DB: ReadonlyMap<string, ItemDef> = new Map<string, ItemDef>(
  ALL_ITEM_DEFS.map((d) => [d.id, d]),
);

/** Looks a def up by id. `null` for anything unknown — never throws, because
 *  the id may have come straight off a client message. */
export function getItemDef(id: string): ItemDef | null {
  return ITEM_DB.get(id) ?? null;
}

/**
 * Cells this container actually holds: the sum of its pouches, or the whole
 * field without a layout. The def-side twin of `gridCapacity`; lives here
 * because `grid.ts` already imports this module.
 */
export function containerCapacity(def: ContainerItemDef): number {
  const layout = def.layout;
  if (layout === undefined) return def.gridW * def.gridH;
  let n = 0;
  for (const z of layout) n += z.w * z.h;
  return n;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * `isTrueMagazine` again — `mods.ts` imports this module, so importing it back
 * would be a cycle. The predicate is the field's presence.
 */
function trueMagazineDef(def: ItemDef | null): MagazineItemDef | null {
  if (def === null || def.kind !== "attachment") return null;
  if (def.attachKind !== "mag" || def.magCapacity === undefined) return null;
  return def as MagazineItemDef;
}

/**
 * Structural sanity check for the roster. Returns human-readable problems;
 * empty means sound. A unit test and a boot assertion.
 */
export function validateItemDb(): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const d of ALL_ITEM_DEFS) {
    const where = `item "${d.id}"`;
    if (d.id.length === 0) problems.push("an item has an empty id");
    if (seen.has(d.id)) problems.push(`duplicate item id "${d.id}"`);
    seen.add(d.id);
    if (ITEM_DB.get(d.id) !== d) problems.push(`${where} is not the one the registry holds`);

    if (d.name.length === 0) problems.push(`${where} has an empty name`);
    if (d.shortName.length === 0) problems.push(`${where} has an empty shortName`);
    if (!Number.isInteger(d.w) || d.w < 1) problems.push(`${where} has w ${d.w} (must be an integer >= 1)`);
    if (!Number.isInteger(d.h) || d.h < 1) problems.push(`${where} has h ${d.h} (must be an integer >= 1)`);
    if (!Number.isInteger(d.stackMax) || d.stackMax < 1) {
      problems.push(`${where} has stackMax ${d.stackMax} (must be an integer >= 1)`);
    }
    if (!(d.weightKg >= 0) || !Number.isFinite(d.weightKg)) {
      problems.push(`${where} has a nonsense weightKg`);
    }
    if (!(d.valueEuro >= 0) || !Number.isFinite(d.valueEuro)) {
      problems.push(`${where} has a nonsense valueEuro`);
    }
    // Stacking a unique object (a gun, a vest, a med with charges left) would
    // need per-unit state the stack cannot carry, so only plain goods stack.
    if (d.stackMax > 1 && d.kind !== "ammo" && d.kind !== "loot") {
      problems.push(`${where} is a ${d.kind} and must not stack`);
    }

    switch (d.kind) {
      case "weapon": {
        const s = d.stats;
        if (s.rpm <= 0) problems.push(`${where} has rpm <= 0`);
        if (!Number.isInteger(s.magSize) || s.magSize < 1) problems.push(`${where} has a bad magSize`);
        if (s.reloadSec <= 0) problems.push(`${where} has reloadSec <= 0`);
        if (s.muzzleVelocity <= 0) problems.push(`${where} has muzzleVelocity <= 0`);
        if (s.falloffStartM >= s.falloffEndM) problems.push(`${where} has falloffStart >= falloffEnd`);
        if (!(s.falloffMinMult > 0) || s.falloffMinMult > 1) {
          problems.push(`${where} has falloffMinMult outside (0, 1]`);
        }
        if (s.zeroRangeM <= 0) problems.push(`${where} has zeroRangeM <= 0`);
        // A zero or NaN would reach the aim system as "this gun does not kick".
        if (!Number.isFinite(d.recoil) || d.recoil <= 0) {
          problems.push(`${where} has recoil ${d.recoil} (must be finite and > 0)`);
        }
        // `wearExtraSpreadDeg` divides by it and the bench prices against it.
        if (!Number.isFinite(d.maxDurability) || !(d.maxDurability > 0)) {
          problems.push(`${where} has maxDurability ${d.maxDurability} (must be finite and > 0)`);
        }
        if (!Number.isFinite(s.ergonomics) || s.ergonomics < 0 || s.ergonomics > 100) {
          problems.push(`${where} has ergonomics ${s.ergonomics} outside [0, 100]`);
        }
        // `adsTimeSec` is derived; an authored one that disagrees was edited by
        // hand. Tolerance for the float round-trip only.
        if (Math.abs(s.adsTimeSec - adsTimeForErgo(s.ergonomics)) > 1e-9) {
          problems.push(
            `${where} has adsTimeSec ${s.adsTimeSec}, but ergonomics ${s.ergonomics} means ${adsTimeForErgo(s.ergonomics)}`,
          );
        }
        break;
      }
      case "ammo": {
        if (!(d.damage > 0)) problems.push(`${where} has damage <= 0`);
        if (!(d.penPower >= 0)) problems.push(`${where} has a negative penPower`);
        if (!(d.velocityMult > 0)) problems.push(`${where} has velocityMult <= 0`);
        if (!Number.isInteger(d.pellets) || d.pellets < 1) problems.push(`${where} has a bad pellets`);
        break;
      }
      case "armor": {
        if (!Number.isInteger(d.armorClass) || d.armorClass < 1) {
          problems.push(`${where} has a bad armorClass`);
        }
        if (!(d.maxDurability > 0)) problems.push(`${where} has maxDurability <= 0`);
        if (d.zones.length === 0) problems.push(`${where} covers no zones`);
        if (d.equipSlot === "helmet" && !d.zones.includes("head")) {
          problems.push(`${where} is a helmet that does not cover the head`);
        }
        if (d.equipSlot === "vest" && d.zones.includes("head")) {
          problems.push(`${where} is a vest that covers the head`);
        }
        break;
      }
      case "med": {
        if (!(d.useTimeSec > 0)) problems.push(`${where} has useTimeSec <= 0`);
        if (!Number.isInteger(d.charges) || d.charges < 1) problems.push(`${where} has a bad charges`);
        if (d.effect.t === "heal" && !(d.effect.hpPerCharge > 0)) {
          problems.push(`${where} heals <= 0 hp per charge`);
        }
        break;
      }
      case "grenade": {
        // A non-positive fuse goes off in the hand the tick the pin comes out.
        if (!Number.isFinite(d.fuseSec) || !(d.fuseSec > 0)) {
          problems.push(`${where} has fuseSec ${d.fuseSec} (must be finite and > 0)`);
        }
        // Everything downstream divides by the radius (`blastTargets` falloff).
        if (!Number.isFinite(d.effect.radiusM) || !(d.effect.radiusM > 0)) {
          problems.push(`${where} has radiusM ${d.effect.radiusM} (must be finite and > 0)`);
        }
        if (d.effect.t === "frag") {
          if (!(d.effect.damage > 0)) problems.push(`${where} does damage <= 0`);
        } else if (!(d.effect.durationSec > 0)) {
          // Smoke and flash both measure themselves in seconds, and an effect
          // that lasts none is an item that does nothing at all.
          problems.push(`${where} lasts ${d.effect.durationSec} s (must be > 0)`);
        }
        break;
      }
      case "container": {
        if (!Number.isInteger(d.gridW) || d.gridW < 1) problems.push(`${where} has a bad gridW`);
        if (!Number.isInteger(d.gridH) || d.gridH < 1) problems.push(`${where} has a bad gridH`);
        // The pouch layout: what `placementProblem` and `containerCapacity`
        // assume — never `[]` (absent means one compartment), integral
        // non-empty rectangles inside the field, non-overlapping.
        const layout = d.layout;
        if (layout !== undefined) {
          if (layout.length === 0) {
            problems.push(`${where} has an empty layout (omit it for one open compartment)`);
          }
          // Only sound pouches are compared for overlap.
          const sound: GridRect[] = [];
          for (const z of layout) {
            const at = `(${z.x},${z.y} ${z.w}x${z.h})`;
            if (
              !Number.isInteger(z.x) ||
              !Number.isInteger(z.y) ||
              !Number.isInteger(z.w) ||
              !Number.isInteger(z.h) ||
              z.x < 0 ||
              z.y < 0 ||
              z.w < 1 ||
              z.h < 1
            ) {
              problems.push(`${where} has a bad pouch ${at}`);
              continue;
            }
            if (z.x + z.w > d.gridW || z.y + z.h > d.gridH) {
              problems.push(`${where} has a pouch ${at} outside its ${d.gridW}x${d.gridH} grid`);
              continue;
            }
            for (const o of sound) {
              // Half-open rectangles, exactly as `placementProblem` compares them.
              if (z.x + z.w <= o.x || o.x + o.w <= z.x) continue;
              if (z.y + z.h <= o.y || o.y + o.h <= z.y) continue;
              problems.push(`${where} has overlapping pouches ${at} and (${o.x},${o.y} ${o.w}x${o.h})`);
            }
            sound.push(z);
          }
        }
        // A box holding no more than it occupies is dead weight; a worn
        // container
        // pays no cells while worn so may equal its footprint. Capacity is the
        // pouches when there are pouches.
        const minCells = d.equipSlot === "none" ? d.w * d.h + 1 : d.w * d.h;
        const cells = containerCapacity(d);
        if (cells < minCells) {
          problems.push(`${where} carries fewer cells (${cells}) than it costs (${d.w}x${d.h})`);
        }
        // The kind filter: known kinds, never `[]` (absent means anything),
        // never "container" so a filtered box is always a leaf.
        const filter = d.filter;
        if (filter !== undefined) {
          if (filter.length === 0) {
            problems.push(`${where} has an empty filter (omit it to accept everything)`);
          }
          const seenKinds = new Set<string>();
          for (const kind of filter) {
            if (!ITEM_KINDS.includes(kind)) {
              problems.push(`${where} filters on unknown kind "${kind}"`);
              continue;
            }
            if (seenKinds.has(kind)) problems.push(`${where} lists kind "${kind}" twice`);
            seenKinds.add(kind);
            if (kind === "container") {
              problems.push(`${where} filters on "container" — boxes do not hold boxes`);
            }
          }
        }
        break;
      }
      case "attachment": {
        if (!ATTACH_KINDS.includes(d.attachKind)) {
          problems.push(`${where} has an unknown attachKind "${d.attachKind}"`);
        }
        // Stateless and unique — `attach` relies on stackMax 1.
        if (d.stackMax !== 1) problems.push(`${where} is an attachment and must have stackMax 1`);

        const m = d.modifiers;
        for (const [key, value] of [
          ["hipSpreadMult", m.hipSpreadMult],
          ["recoilMult", m.recoilMult],
          ["muzzleVelocityMult", m.muzzleVelocityMult],
          ["reloadSecMult", m.reloadSecMult],
        ] as const) {
          if (value === undefined) continue;
          // A multiplier of 0 or less would zero or invert a stat; there is no
          // honest attachment that does either.
          if (!(value > 0) || !Number.isFinite(value)) {
            problems.push(`${where} has ${key} ${value} (must be > 0)`);
          }
        }
        // Additive, so negative or zero is fine; only NaN/Infinity is not.
        if (m.ergoAdd !== undefined && !Number.isFinite(m.ergoAdd)) {
          problems.push(`${where} has a non-finite ergoAdd`);
        }
        if (m.zeroRangeSetM !== undefined && !(m.zeroRangeSetM > 0)) {
          problems.push(`${where} has zeroRangeSetM <= 0`);
        }
        if (m.zoom !== undefined) {
          if (!(m.zoom > 0)) problems.push(`${where} has zoom <= 0`);
          // Zoom is an ADS FOV factor; on anything but a sight it is a number
          // nothing would ever read.
          if (d.attachKind !== "optic") problems.push(`${where} has zoom but is not an optic`);
        }

        // --- The magazine fields: `magCapacity` turns a stat block into a
        // stateful object, so everything downstream keys off its presence.
        if (d.magCapacity !== undefined) {
          if (!Number.isInteger(d.magCapacity) || d.magCapacity < 1) {
            problems.push(
              `${where} has magCapacity ${d.magCapacity} (must be an integer >= 1)`,
            );
          }
          // A scope that claimed to hold rounds would be a scope `load` pours
          // cartridges into.
          if (d.attachKind !== "mag") {
            problems.push(`${where} has magCapacity but is a ${d.attachKind}, not a magazine`);
          }
        }
        // Present together or not at all.
        if ((d.caliber === undefined) !== (d.magCapacity === undefined)) {
          problems.push(`${where} must declare caliber and magCapacity together, or neither`);
        }
        if (d.caliber !== undefined && !CALIBER_IDS.includes(d.caliber)) {
          problems.push(`${where} has an unknown caliber "${d.caliber}"`);
        }
        break;
      }
      case "loot":
        // A quest item is worth 0 here, absent from `BASE_VALUE_EUR` and
        // refused
        // by every counter (`validatePrices`/`validateTraders` hold the other
        // ends).
        if (d.questItem === true && d.valueEuro !== 0) {
          problems.push(`${where} is a quest item and must be worth 0 € (it says ${d.valueEuro})`);
        }
        // Food: the clock runs, gains are real fractions, at least one is a
        // gain
        // (else "Eat" is refused every time). A quest item is not food.
        if (d.consume !== undefined) {
          const c = d.consume;
          if (!(c.useTimeSec > 0)) problems.push(`${where} is edible but has useTimeSec <= 0`);
          for (const key of ["energy", "hydration"] as const) {
            const v = c[key];
            if (!Number.isFinite(v) || v < 0 || v > NEEDS_MAX) {
              problems.push(`${where} has a ${key} gain of ${v} (must be 0..${NEEDS_MAX})`);
            }
          }
          if (!(c.energy > 0) && !(c.hydration > 0)) {
            problems.push(`${where} is edible but feeds nothing`);
          }
          if (d.questItem === true) problems.push(`${where} is a quest item and cannot be edible`);
        }
        break;
    }
  }

  // --- Mounting points: one function for weapon and part hosts, so the two
  // cannot disagree. The clauses that differ key off `caliber` (weapons only).
  const checkSlots = (
    ownerId: string,
    slots: readonly WeaponSlotDef[] | undefined,
    caliber: CaliberId | null,
  ): void => {
    if (slots === undefined) return;
    const seenSlots = new Set<AttachKind>();
    for (const slot of slots) {
      const where = `item "${ownerId}" slot "${slot.id}"`;
      if (!ATTACH_KINDS.includes(slot.id)) problems.push(`${where} has an unknown kind`);
      // One slot per kind PER HOST (`mods` is keyed by kind).
      if (seenSlots.has(slot.id)) problems.push(`item "${ownerId}" declares slot "${slot.id}" twice`);
      seenSlots.add(slot.id);
      if (slot.accepts.length === 0) problems.push(`${where} accepts nothing`);

      const seenAccepts = new Set<string>();
      for (const accepted of slot.accepts) {
        if (seenAccepts.has(accepted)) problems.push(`${where} lists "${accepted}" twice`);
        seenAccepts.add(accepted);
        const def = getItemDef(accepted);
        if (def === null) {
          problems.push(`${where} accepts unknown item "${accepted}"`);
        } else if (def.kind !== "attachment") {
          problems.push(`${where} accepts "${accepted}", which is a ${def.kind}`);
        } else if (def.attachKind !== slot.id) {
          problems.push(`${where} accepts "${accepted}", which is a ${def.attachKind}`);
        }
      }

      // --- Vital: a slot nothing fits that the gun cannot fire without is a
      // gun
      // that never fires. Already a problem above; repeated for legibility.
      if (slot.vital === true && slot.accepts.length === 0) {
        problems.push(`${where} is vital but accepts nothing, so the gun can never fire`);
      }

      // --- The factory part must be one the slot takes, or `mintFactoryMods`
      // would hand out a gun the bench refuses and every fold ignores.
      const factoryId = slot.factoryDefId;
      if (factoryId !== undefined) {
        if (slot.id === "mag") {
          problems.push(
            `${where} declares factoryDefId, but a magazine well names its factory box by listing it first`,
          );
        }
        const factory = getItemDef(factoryId);
        if (factory === null) {
          problems.push(`${where} ships with unknown item "${factoryId}"`);
        } else if (!slot.accepts.includes(factoryId)) {
          problems.push(`${where} ships with "${factoryId}", which it does not accept`);
        }
      }

      // --- The magazine well: a PART may not have one, which keeps the rounds
      // path flat — "the box in this gun" is one lookup on the gun.
      if (slot.id !== "mag") continue;
      if (caliber === null) {
        problems.push(`${where} is a magazine well on an attachment — rounds are gun-level only`);
        continue;
      }

      // Magazines only: a stateless part in the well is a gun with `magSize` 0.
      for (const accepted of slot.accepts) {
        const def = getItemDef(accepted);
        if (def === null || trueMagazineDef(def) !== null) continue;
        problems.push(`${where} accepts "${accepted}", which is not a true magazine`);
      }

      // One calibre: a magazine that fed something other than its host could
      // be filled at one gun and emptied into another.
      for (const accepted of slot.accepts) {
        const mag = trueMagazineDef(getItemDef(accepted));
        if (mag === null) continue;
        if (mag.caliber !== caliber) {
          problems.push(
            `${where} accepts "${accepted}", which feeds ${mag.caliber} rather than ${caliber}`,
          );
        }
      }
    }
  };

  for (const w of WEAPON_ITEMS) checkSlots(w.id, w.slots, w.caliber);
  for (const a of ATTACHMENT_ITEMS) checkSlots(a.id, a.slots, null);

  // --- Depth and cycle check: proves the CONTENT cannot express a third level
  // (a part reachable at `MAX_MOD_DEPTH` declares no slots), so every runtime
  // truncation is unreachable by legitimate data. A cycle shows up as the
  // over-depth it produces, reported by name.
  const walkHosts = (
    label: string,
    slots: readonly WeaponSlotDef[] | undefined,
    depth: number,
    path: readonly string[],
  ): void => {
    for (const slot of slots ?? []) {
      for (const accepted of slot.accepts) {
        const part = getItemDef(accepted);
        if (part === null || part.kind !== "attachment") continue;
        const partSlots = part.slots;
        if (partSlots === undefined || partSlots.length === 0) continue;
        const at = depth + 1;
        if (path.includes(part.id)) {
          problems.push(`${label} closes a hosting cycle back to "${part.id}"`);
          continue;
        }
        if (at >= MAX_MOD_DEPTH) {
          problems.push(
            `${label} can host "${part.id}" at depth ${at}, where a part may declare no slots of its own`,
          );
          continue;
        }
        walkHosts(`item "${part.id}"`, partSlots, at, [...path, part.id]);
      }
    }
  };
  for (const w of WEAPON_ITEMS) walkHosts(`item "${w.id}"`, w.slots, 0, [w.id]);

  // --- Calibers round-trip: every gun loadable, every round fireable -------
  for (const c of CALIBER_IDS) {
    const guns = WEAPON_ITEMS.filter((w) => w.caliber === c);
    const rounds = AMMO_ITEMS.filter((a) => a.caliber === c);
    if (guns.length > 0 && rounds.length === 0) problems.push(`caliber "${c}" has weapons but no ammo`);
    if (rounds.length > 0 && guns.length === 0) problems.push(`caliber "${c}" has ammo but no weapon`);
  }
  for (const w of WEAPON_ITEMS) {
    if (!CALIBER_IDS.includes(w.caliber)) problems.push(`item "${w.id}" has an unknown caliber`);
  }
  for (const a of AMMO_ITEMS) {
    if (!CALIBER_IDS.includes(a.caliber)) problems.push(`item "${a.id}" has an unknown caliber`);
  }

  return problems;
}
